import UsageAnalyticsGate from 'components/UsageAnalyticsGate';
import Layout, { largeScreen, MaintenanceMessage, ResumingMessage } from 'components/Layout';
import React, {
  Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Login from 'Login';
import CognitoLogin from 'CognitoLogin';
import { defaultMyStats, myStatsType } from 'components/stats/SeriesInputs2';
import { useMutation } from '@apollo/client';
import { REFRESH_TOKEN } from 'mutation/authentication';
import { REFRESH_TOKEN_INTERVAL } from 'components/constants';
import { StoryType } from './components/Breadcrumbs';
import { TeamType } from './models/team';
import CLIENT_SETTINGS from './lib/client_settings';
import FullscreenWebplayerRecorder from './components/webPlayer/FullscreenWebplayerRecorder';
import { GlobalAppStateProvider } from './context/GlobalAppStateContext';
import { handleLogout } from './components/Account';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import i18n from './lib/i18n';
import useHealthPolling from './custom_hooks/useHealthPolling';
import { useGlobalState } from './custom_hooks/useGlobalState';
import { APP_STATES, AppState } from './lib/global';

export interface WebsocketRoomsState {
  label?: string,
  msg?: string,
  count: number,
  value: string | number,
  room: 'user' | 'team',
  content?: {
    id?: number,
    filename?: string,
    reason?: string | null,
  },
}

export const AuthenticatorContext = React.createContext<{
  isTokenValid: boolean;
  setIsTokenValid: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  isTokenValid: false,
  setIsTokenValid: () => false,
});

export const WebsocketRoomsContext = React.createContext<{
  messages: { [k: string]: WebsocketRoomsState };
  setMessages: Dispatch<SetStateAction<{ [k: string]: WebsocketRoomsState }>>;
}>({
  messages: {},
  setMessages: () => {
  },
});

export const TeamContext = React.createContext<{
  globalTeam?: TeamType;
  setGlobalTeam:(team: TeamType) => void;
    }>({
      globalTeam: undefined,
      setGlobalTeam: () => {
      },
    });

export const BreadCrumbsContext = React.createContext<{
  breadCrumbs?: StoryType;
  setBreadCrumbs:(story: StoryType) => void;
    }>({
      breadCrumbs: undefined,
      setBreadCrumbs: () => {
      },
    });

export const DocumentsContext = React.createContext<{
  refetchDocumentsStatus?: number;
  setRefetchDocumentsStatus:(status: number) => void;
    }>({
      refetchDocumentsStatus: 0,
      setRefetchDocumentsStatus: () => {
      },
    });

export const MyStatsContext = React.createContext<{
  myStats?: myStatsType;
  setMyStats:(stats: any) => void;
    }>({
      myStats: undefined,
      setMyStats: () => {
      },
    });

export const FilesTrackFilterContext = React.createContext<{
  incomingFileId?: string | null;
  setIncomingFileId: React.Dispatch<React.SetStateAction<string>>;
    }>({
      incomingFileId: '',
      setIncomingFileId: () => {
      },
    });

export const LoadingContext = React.createContext<{
  isLoading: boolean;
  setIsLoading:(value: boolean) => void;
    }>({
      isLoading: false,
      setIsLoading: () => {
      },
    });

export type ThemeOptions = {
  noHeaderMargins: boolean,
  sideBarOpen?: boolean,
  isTemplateSelectorActive: boolean,
}

export const ThemeOptionsContext = React.createContext<{
  themeOptions: ThemeOptions;
  setThemeOptions:(options: ThemeOptions) => void;
    }>({
      themeOptions: {
        noHeaderMargins: false,
        sideBarOpen: largeScreen(),
        isTemplateSelectorActive: true,
      },
      setThemeOptions: () => {
      },
    });

const WebsocketRoomsContextProvider: React.FC = ({ children }) => {
  const [messages, setMessagesData] = useState<{ [k: string]: WebsocketRoomsState }>({});
  const setMessages = useCallback((msgs) => {
    setMessagesData(msgs);
  }, []);

  const messagesValue = useMemo(() => ({
    messages,
    setMessages,
  }), [messages]);

  return (
    <WebsocketRoomsContext.Provider value={messagesValue}>
      {children}
    </WebsocketRoomsContext.Provider>
  );
};

const AuthenticatorContextProvider: React.FC = ({ children }) => {
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [backendChanged, setBackendChanged] = useState(false);
  const refreshJwtToken = localStorage.getItem('exelio_refresh_token');
  const isLoginPage = window.location.href.includes('login');

  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();

  const authenticatorValue = useMemo(() => ({
    isTokenValid,
    setIsTokenValid,
  }), [isTokenValid]);
  const now = Math.floor(Date.now() / 1000);
  const refreshTokenExpiration = Number(localStorage.getItem('exelio_refresh_token_exp') || 0);
  const isRefreshTokenExpired = refreshTokenExpiration < now;
  // usiamo i ref per mantenere tra i rerender
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);

  const [appState] = useGlobalState(AppState);
  const shouldPause =
    appState === APP_STATES.maintenance ||
    appState === APP_STATES.down;

  const [refreshToken] = useMutation(REFRESH_TOKEN, {
    variables: {
      refreshToken: refreshJwtToken,
    },
    onCompleted(data) {
      if (!data.res) {
        enqueueSnackbar(
          t('login.errors.errorRefreshing', 'Error while refreshing token.'),
          {
            variant: 'error',
            autoHideDuration: 5000,
          },
        );
        handleLogout();
        return;
      }

      console.log('Token refreshed successfully');
      const {
        token: newToken,
        refreshToken: newRefreshToken,
        refreshExpiresIn,
      } = data.res;
      localStorage.setItem('exelio_token', newToken);
      localStorage.setItem('exelio_refresh_token', newRefreshToken);
      localStorage.setItem('exelio_refresh_token_exp', JSON.stringify(refreshExpiresIn));
      const translations = i18n.getResourceBundle(i18n.language, 'translation');
      if (!translations || Object.keys(translations).length === 0) {
        i18n.reloadResources();
      }
      setTimeout(() => setIsTokenValid(true), 250);
      setBackendChanged(false);
    },
    onError(error) {
      // Al risveglio della tab partono più refresh insieme: il refresh token è
      // single-use, quindi quello "perdente" fallisce con un token ormai ruotato.
      // Se in localStorage c'è già un token diverso, un refresh concorrente è
      // andato a buon fine: ignoriamo l'errore per non mostrare un falso Login.
      const currentRefreshToken = localStorage.getItem('exelio_refresh_token');
      if (currentRefreshToken && currentRefreshToken !== refreshJwtToken) {
        console.warn('Stale refresh error ignored: token already rotated by a concurrent refresh');
        return;
      }
      if (error.message) {
        const trimmedErrorMessage = error.message.trim();
        if (trimmedErrorMessage === 'Invalid refresh token') {
          setBackendChanged(true);
        }
      }
      console.error('Error refreshing token:', error);
      setTimeout(() => setIsTokenValid(false), 250);
    },
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isLoginPage && refreshJwtToken && !isRefreshTokenExpired) {
        refreshToken(); // refresh token immediately when the tab becomes active (ex: after pc suspension)
        // cognitoRefreshToken();
      }
    };

    if (!shouldPause && !isLoginPage && refreshJwtToken && !isRefreshTokenExpired) {
      // console.log("cognito refresh token interval: ", COGNITO_REFRESH_TOKEN_INTERVAL);

      refreshToken(); // refresh immediately on initial load

      // cognitoRefreshToken();

      intervalId.current = setInterval(() => refreshToken(), REFRESH_TOKEN_INTERVAL); // periodic refresh (3 minutes)

      // console.log("set period refresh token for cognito every 4 minutes");
      // @todo valuta se va richiamato anche se già impostato (in base alle deps che usi riga 266 di useEffect)
      // se già impostato non deve essere cambiato usa invece
      /*
      if (!intervalId.current) {
        intervalId.current = setInterval(() => cognitoRefreshToken(), 240000);
      }
       */
      // intervalId.current = setInterval(() => cognitoRefreshToken(), 240000);

      // listen to visibility change events
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoginPage, refreshJwtToken, isRefreshTokenExpired, refreshToken]);

  return (
    <AuthenticatorContext.Provider value={authenticatorValue}>
      {(isRefreshTokenExpired || backendChanged) ? <Login /> : children}
    </AuthenticatorContext.Provider>
  );
};

const Authenticator = () => {
  const [globalTeam, setGlobalTeam] = useState<TeamType | undefined>();
  const [refetchDocumentsStatus, setRefetchDocumentsStatus] = useState(0);
  const [myStats, setMyStats] = useState<myStatsType | undefined>(defaultMyStats);
  const [incomingFileId, setIncomingFileId] = useState('');
  const [breadCrumbs, setBreadCrumbs] = useState<StoryType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [themeOptions, setThemeOptions] = useState<ThemeOptions>({
    noHeaderMargins: false,
    isTemplateSelectorActive: true,
    sideBarOpen: largeScreen(),
  });

  const teamValue = useMemo(() => ({
    globalTeam,
    setGlobalTeam,
  }), [globalTeam, setGlobalTeam]);

  const documentsValue = useMemo(() => ({
    refetchDocumentsStatus,
    setRefetchDocumentsStatus,
  }), [refetchDocumentsStatus, setRefetchDocumentsStatus]);

  const myStatsValue = useMemo(() => ({
    myStats,
    setMyStats,
  }), [myStats, setMyStats]);

  const filesTrackFilterValue = useMemo(() => ({
    incomingFileId,
    setIncomingFileId,
  }), [incomingFileId, setIncomingFileId]);

  const breadCrumbsValue = useMemo(() => ({
    breadCrumbs,
    setBreadCrumbs,
  }), [breadCrumbs, setBreadCrumbs]);

  const isLoadingValue = useMemo(() => ({
    isLoading,
    setIsLoading,
  }), [isLoading, setIsLoading]);

  const themeOptionsValue = useMemo(() => ({
    themeOptions,
    setThemeOptions,
  }), [themeOptions, setThemeOptions]);

  const isTestingEnv = CLIENT_SETTINGS.public.environment === 'testing';

  useHealthPolling(); // avvia il polling una volta sola qui
  const [appState] = useGlobalState(AppState);

  if (appState === APP_STATES.maintenance || appState === APP_STATES.down) {
    return <MaintenanceMessage />;
  }

  if (appState === APP_STATES.restoration) {
    return <ResumingMessage />;
  }

  return (
    <TeamContext.Provider value={teamValue}>
      <DocumentsContext.Provider value={documentsValue}>
        <MyStatsContext.Provider value={myStatsValue}>
          <FilesTrackFilterContext.Provider value={filesTrackFilterValue}>
            <BreadCrumbsContext.Provider value={breadCrumbsValue}>
              <LoadingContext.Provider value={isLoadingValue}>
                <ThemeOptionsContext.Provider value={themeOptionsValue}>
                  <WebsocketRoomsContextProvider>
                    <AuthenticatorContextProvider>
                      <GlobalAppStateProvider>
                        <BrowserRouter>
                          <UsageAnalyticsGate />
                          <Routes>
                            <Route path="/*" element={<Layout />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/fullscreen-session-recorder" element={<FullscreenWebplayerRecorder />} />
                            {
                              isTestingEnv
                              && <Route path="/cognito" element={<CognitoLogin />} />
                            }
                          </Routes>
                        </BrowserRouter>
                      </GlobalAppStateProvider>
                    </AuthenticatorContextProvider>
                  </WebsocketRoomsContextProvider>
                </ThemeOptionsContext.Provider>
              </LoadingContext.Provider>
            </BreadCrumbsContext.Provider>
          </FilesTrackFilterContext.Provider>
        </MyStatsContext.Provider>
      </DocumentsContext.Provider>
    </TeamContext.Provider>
  );
};

export default Authenticator;
